import { useMemo, useState } from 'react';
import { AlertTriangle, AlertOctagon, Info, X } from 'lucide-react';
import { cn } from '../lib/cn';
import type { Broadcast, NotificationSeverity } from '../mocks/clientBatch';
import { useMyBroadcasts } from '../api/platform';

const SEVERITY_BG: Record<NotificationSeverity, string> = {
  info: 'bg-zinc-100 text-zinc-900 dark:bg-zinc-100 dark:text-zinc-900',
  warning: 'bg-amber-100 text-amber-900 dark:bg-amber-900/30 dark:text-amber-100',
  critical: 'bg-rose-100 text-rose-900 dark:bg-rose-900/30 dark:text-rose-100',
};

const SEVERITY_ICON: Record<NotificationSeverity, typeof Info> = {
  info: Info,
  warning: AlertTriangle,
  critical: AlertOctagon,
};

/**
 * Top-of-page dismissible banner. Shows the first pending broadcast that has
 * `top_banner` in its delivery channels and is still within `pinnedUntil`.
 *
 * State is in-component: closing hides it for this client load (mock; not persisted).
 */
export function TopPageBanner() {
  const { data: broadcasts = [] } = useMyBroadcasts();
  const [dismissedIds, setDismissedIds] = useState<Set<string>>(() => new Set());

  const current: Broadcast | undefined = useMemo(() => {
    const now = new Date();
    return broadcasts.find((b) => {
      if (dismissedIds.has(b.id)) return false;
      if (!(b.deliveryChannels ?? []).includes('top_banner')) return false;
      if (!b.sentAt) return false;
      if (b.pinnedUntil) {
        try {
          if (new Date(b.pinnedUntil) < now) return false;
        } catch {
          /* keep */
        }
      }
      return true;
    });
  }, [broadcasts, dismissedIds]);

  if (!current) return null;

  const Icon = SEVERITY_ICON[current.severity];

  return (
    <div
      role="status"
      className={cn(
        'w-full border-b border-zinc-200 dark:border-zinc-700 px-4 sm:px-5 py-2 flex items-center gap-3 text-sm',
        SEVERITY_BG[current.severity],
      )}
    >
      <Icon className="w-4 h-4 flex-shrink-0" />
      <div className="flex-1 min-w-0 truncate">
        <span className="font-medium">{current.title}</span>
        {current.linkUrl && (
          <>
            {' '}
            <a
              href={current.linkUrl}
              target="_blank"
              rel="noreferrer"
              className="underline font-medium hover:no-underline"
            >
              Learn more →
            </a>
          </>
        )}
      </div>
      <button
        type="button"
        onClick={() =>
          setDismissedIds((prev) => {
            const next = new Set(prev);
            next.add(current.id);
            return next;
          })
        }
        aria-label="Dismiss banner"
        className="flex-shrink-0 w-6 h-6 grid place-items-center rounded hover:bg-current/10 transition"
      >
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}
